import { useIntl } from '@/intl'
import { LazyImage } from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { mqMin, tokPx } from '@sunwebgroup/ui-components'
import { Badge, Button, Rating, Typography } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'
import { AccoCardContainer, CardFooter, CardHeader, CardMain } from './AccoCard'
import { useAccoDetailsById } from './hooks'
import { MapAccoCardLoading } from './MapAccoCardLoading'

const ImageContainer = styled.div`
  aspect-ratio: 1/1;
  width: 115px;
  flex-shrink: 0;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    width: 100%;
    aspect-ratio: 3/2;
  }
`

const AccoName = styled(Typography)`
  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-g-regular')};
  }
`

const AccoDetails = styled(Typography)`
  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('body-s-regular-tight')};
  }
`

const RatingWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokPx('primitives_space_8')};
`

const AccoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${tokPx('primitives_space_2')};
`

const StyledButton = styled(Button)`
  ${Button.modSize('S')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Button.modSize('M')};
  }
`

export function MapAccoCard({ accoId }: { accoId: string }) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { accoDetails, isLoading } = useAccoDetailsById(accoId)

  if (isLoading) {
    return <MapAccoCardLoading />
  }

  if (!accoDetails) {
    return null
  }

  const handleBookNowClick = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Button',
        description: 'Clicked on book now button in map card',
        result: `Map card for ${accoDetails.name} is clicked`,
      }),
    )
  }

  return (
    <AccoCardContainer>
      <ImageContainer>
        <LazyImage src={accoDetails.imageUrl ?? ''} srcSet={[320]} alt={accoDetails.name ?? ''} />
      </ImageContainer>
      <CardMain>
        <CardHeader>
          <RatingWrapper>
            <Rating rating={accoDetails.rating} />
            {accoDetails.userRating > 0 && <Badge>{accoDetails.userRating}</Badge>}
          </RatingWrapper>
          <AccoWrapper>
            <AccoName $variant="display-i-regular" $color="semantics_content_color">
              {accoDetails.name}
            </AccoName>
            <AccoDetails $variant="body-xs-regular-tight" $color="semantics_content_secondary_color">
              {[accoDetails.city, accoDetails.region, accoDetails.country].join(', ')}
            </AccoDetails>
          </AccoWrapper>
        </CardHeader>
        <CardFooter>
          <StyledButton
            as="a"
            href={accoDetails.url}
            $type="primary"
            onClick={handleBookNowClick}
          >
            {intl.formatMessage({ id: 'destination_map_detail_card_cta' })}
          </StyledButton>
        </CardFooter>
      </CardMain>
    </AccoCardContainer>
  )
}

MapAccoCard.displayName = 'MapAccoCard'
